import { BRIDGE_VERSION, type Command, event } from '@budovan/bridge';
import { type Bridge, createBridge } from './adapter/bridge.ts';
import { ManifestError, connectLive, fetchManifest } from './adapter/platform.ts';
import { type Metric, type PlaceState, formatReading, rate, tintFor } from './adapter/state.ts';
import { type LabelStatus, createViewer } from './core/viewer.ts';
import { type Panel, createPanel } from './ui/panel.ts';
import { ERROR_TYPE, initErrorReporting, reportError, setSentryTags } from './errorReporting.ts';
import './style.css';

/* Entry point: one URL scheme for the app WebView (?app=1, UI hidden, bridge on; ?warm=1 only fills
 * the cache), public pages and embeds. ?building=B0001 picks the building. The app hands the token over
 * the bridge (setAccessToken) once the page has loaded, and the viewer waits a moment for it before its
 * first read; for local testing it may also come in the URL fragment (#access_token=…, never sent to a
 * server, dropped from the address at once). */

const params = new URLSearchParams(window.location.search);
const buildingCode = params.get('building') ?? 'B0001';
const inApp = params.get('app') === '1';
const warm = params.get('warm') === '1';
let token = new URLSearchParams(window.location.hash.slice(1)).get('access_token');
let tokenArrived: (() => void) | null = null;
// Listening starts before the first await: the app hands the token over once the page has loaded,
// which can be before config.json is read. Until the viewer is set up, only the token is taken.
let onCommand = (command: Command): void => {
  if (command.type !== 'setAccessToken') return;
  token = command.payload.token;
  tokenArrived?.();
};
const bridge = createBridge((command) => onCommand(command));
type DeployConfig = { apiUrl?: string; sentryDsn?: string; sentryEnvironment?: string };
const config: DeployConfig = await fetch('/config.json', { cache: 'no-cache' })
  .then((response) => (response.ok ? (response.json() as Promise<DeployConfig>) : {}))
  .catch(() => ({}));
const apiUrl = config.apiUrl ?? 'http://localhost:3000';
// Out of the address bar, so neither error reports nor traces (which record the page URL) carry it.
if (window.location.hash) history.replaceState(null, '', window.location.pathname + window.location.search);
initErrorReporting({ ...config, apiUrl });
setSentryTags({ building: buildingCode, inApp: String(inApp) });

// How long an app page gets to hand over the token before the first read goes out anonymous.
const TOKEN_WAIT_MS = 3_000;
const tokenFromApp = (): Promise<void> =>
  token || !(inApp || warm)
    ? Promise.resolve()
    : new Promise((resolve) => {
        tokenArrived = resolve;
        setTimeout(resolve, TOKEN_WAIT_MS);
      });

// ?warm=1: the app fills the HTTP cache from Home once it is idle (a hidden WebView or iframe), so the
// first real open reads the bundle and the model from disk. The modules are fetched and parsed by the
// imports above; this fetches the model too, then stops before any engine, canvas or socket exists.
if (warm) {
  await tokenFromApp();
  const manifest = await fetchManifest(apiUrl, buildingCode, token).catch(() => null);
  if (manifest?.model) await fetch(new URL(manifest.model.url, window.location.href)).catch(() => null);
  // The standard material's shaders (and their includes) are chunks of their own, fetched on first draw.
  await Promise.all([
    import('@babylonjs/core/Shaders/default.vertex'),
    import('@babylonjs/core/Shaders/default.fragment'),
  ]).catch(() => null);
  await new Promise<never>(() => undefined);
}

const canvas = document.querySelector('canvas') as HTMLCanvasElement;
const panelRoot = document.querySelector('#panel') as HTMLElement;
panelRoot.hidden = inApp;
const viewer = createViewer(canvas);

let states = new Map<string, PlaceState>();
// Free and booked for the range the app is booking in; wins over the live state while set.
let availability = new Map<string, 'free' | 'booked'>();
let selected: string | null = null;
let placeNames = new Map<string, string>();
let labelCodes: string[] = [];
// The Layers sheet's reading: while set, places are tinted and tagged by how it rates, not by booking.
let metric: Metric | null = null;
let mappedCodes: string[] = [];
// "You are here": the app's choice once it makes one, until then the building's lobby.
let startCode: string | null = null;
let startChosen = false;
// Tags show the place's name and whether it is free in the range being booked (else its live state);
// with a reading chosen, the name and the reading.
const relabel = (): void => {
  viewer.setLabels(
    labelCodes.flatMap((code): { code: string; text: string; status: LabelStatus }[] => {
      const name = placeNames.get(code);
      if (!name) return [];
      if (metric) {
        const reading = states.get(code)?.readings[metric];
        return reading
          ? [
              {
                code,
                text: `${name} · ${formatReading(metric, reading.value)}`,
                status: rate(metric, reading.value),
              },
            ]
          : [];
      }
      const status = availability.size > 0 ? availability.get(code) : states.get(code)?.booking;
      return status === 'free' || status === 'booked' ? [{ code, text: name, status }] : [];
    }),
  );
};
let closeLive: (() => void) | null = null;
let bridgeSend: Bridge['send'] = () => undefined;

const repaint = (codes: Iterable<string>): void => {
  // While the app books a range, that range decides: a place it left out (filtered) is neutral, never
  // coloured by its live state, which is about now and not the range.
  const booking = availability.size > 0;
  for (const code of codes) {
    if (metric && code !== selected) {
      const reading = states.get(code)?.readings[metric];
      viewer.tint(code, reading ? rate(metric, reading.value) : 'neutral');
      continue;
    }
    const status = availability.get(code);
    const state = states.get(code);
    let shown = state;
    if (status) shown = { code, readings: {}, ...state, booking: status };
    else if (booking) shown = { code, readings: {}, booking: 'not_bookable' };
    viewer.tint(code, tintFor(shown, code === selected));
  }
};

// The model loads once: start() runs again when the app hands over a token, and only the data refreshes.
let modelLoad: Promise<void> | null = null;
let started = false;
let panel: Panel | null = null;

function select(code: string): void {
  const previous = selected;
  selected = code;
  repaint(previous ? [previous, code] : [code]);
  viewer.focus(code);
  panel?.showDetail(code);
  bridgeSend(event('placeTapped', { code }));
}
// While a route is drawn, a tap does not select: the app shows the route, not a place card.
let routing = false;
viewer.onPick((code) => {
  if (!routing) select(code);
});
(window as unknown as { budovanTwin: { select(code: string): void } }).budovanTwin = { select };

async function start(): Promise<void> {
  const manifest = await fetchManifest(apiUrl, buildingCode, token);
  document.title = `${manifest.building.name} · Budovan`;
  const mapped = manifest.places.filter((place) => place.nodeName);
  if (manifest.model && !modelLoad) {
    modelLoad = viewer
      .load(
        new URL(manifest.model.url, window.location.href).toString(),
        Object.fromEntries(mapped.map((place) => [place.nodeName as string, place.code])),
      )
      .catch((error: unknown) => {
        modelLoad = null;
        throw error;
      });
  }
  await modelLoad;
  placeNames = new Map(manifest.places.map((place) => [place.code, place.name]));
  mappedCodes = mapped.map((place) => place.code);
  if (!startChosen) startCode = mapped.find((place) => place.kind === 'lobby')?.code ?? null;
  // Also when the app chose one before the model was there to put it on.
  viewer.setStart(startCode);
  repaint(mappedCodes);
  relabel();
  panel ??= inApp
    ? null
    : createPanel(panelRoot, manifest, {
        floor: (number) => viewer.showFloor(number),
        select: (code) => select(code),
      });
  closeLive?.();
  if (token && manifest.mode === 'signed_in') {
    closeLive = connectLive(apiUrl, buildingCode, token, (next, changed) => {
      states = next;
      repaint(changed);
      if (metric) relabel();
      panel?.setStates(states);
    });
  }
  setSentryTags({ mode: manifest.mode });
  bridgeSend(event('ready', { bridgeVersion: BRIDGE_VERSION, buildingCode, mode: manifest.mode }));
}

onCommand = (command) => {
  switch (command.type) {
    case 'setAccessToken':
      token = command.payload.token;
      // Before the first start the token is simply picked up by it; after, the data loads again signed in.
      if (started) start().catch(failed);
      else tokenArrived?.();
      break;
    case 'showFloor':
      viewer.showFloor(command.payload.number);
      bridgeSend(event('floorChanged', { number: command.payload.number }));
      break;
    case 'showPlace':
      select(command.payload.code);
      break;
    case 'setView':
      viewer.setView(command.payload.mode);
      break;
    case 'setLabels':
      labelCodes = command.payload.codes;
      relabel();
      break;
    case 'clearSelection': {
      const previous = selected;
      selected = null;
      if (previous) repaint([previous]);
      break;
    }
    case 'highlight':
      repaint(command.payload.codes);
      break;
    case 'setAvailability': {
      const previous = [...availability.keys()];
      availability = new Map([
        ...command.payload.free.map((code) => [code, 'free'] as const),
        ...command.payload.booked.map((code) => [code, 'booked'] as const),
      ]);
      repaint(new Set([...previous, ...availability.keys()]));
      relabel();
      break;
    }
    case 'setFilter':
      break;
    case 'setMetric':
      metric = command.payload.metric;
      repaint(mappedCodes);
      relabel();
      break;
    case 'setStart':
      startChosen = true;
      startCode = command.payload.code;
      viewer.setStart(startCode);
      break;
    case 'navigate': {
      const from = command.payload.from ?? startCode;
      if (!from) {
        bridgeSend(event('routeFailed', { reason: 'no_start' }));
        break;
      }
      const found = viewer.navigate(from, command.payload.to);
      routing = typeof found !== 'string';
      if (typeof found === 'string') bridgeSend(event('routeFailed', { reason: found }));
      else
        bridgeSend(
          event('routeReady', {
            metres: Math.round(found.metres),
            seconds: Math.round(found.seconds),
            steps: found.legs.slice(0, 21).map((leg) => ({
              kind: leg.kind,
              floor: leg.floor,
              toFloor: leg.toFloor,
              metres: Math.round(leg.metres),
            })),
          }),
        );
      break;
    }
    case 'showRouteStep':
      viewer.showRouteStep(command.payload.index);
      break;
    case 'clearRoute':
      routing = false;
      viewer.clearRoute();
      break;
  }
};
bridgeSend = bridge.send;

function failed(error: unknown): void {
  // An unknown building, an expired token or a device offline is expected and stays out of Sentry.
  if (!(error instanceof ManifestError) || error.serverFault) reportError(error, ERROR_TYPE.load);
  bridgeSend(
    event('error', { code: 'LOAD_FAILED', message: error instanceof Error ? error.message : 'unknown' }),
  );
  if (!inApp) panelRoot.textContent = 'This building could not be loaded.';
}

await tokenFromApp();
started = true;
start().catch(failed);
